// 🔧 settings_qr.js
// ✅ Purpose: Admin-only QR Display Settings page
// ✅ Features:
// - Standalone replacement for old QR Settings tab
// - Loads shared event settings from Firestore
// - Controls whether QR print cards use the event image background
// - Uses standard admin layout
// - Shows success/error notifications at the top of the page
// - Auto-clears notifications after 20 seconds

import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useRole } from "../../role_context";
import { useIsEventManager } from "../../hooks/use_is_event_manager";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";

const SettingsQr = () => {
  const { eventId, eventLoading, ready } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole } = useRole();
  const isManager = useIsEventManager(eventId);

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [useImageOnQr, setUseImageOnQr] = useState(true);
  const [saving, setSaving] = useState(false);

  const canAccess =
    ["admin", "manager"].includes(role) ||
    eventRole === "manager" ||
    isManager;

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    const loadSettings = async () => {
      if (!eventId) return;

      try {
        console.log("📷 Loading QR display settings for event:", eventId);

        const ref = doc(db, "events", eventId, "settings", "voting");
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() : {};

        setSettings(data);
        setUseImageOnQr(data.useImageOnQr !== false);
      } catch (err) {
        console.error("❌ Failed to load QR display settings:", err);

        setStatus({
          type: "error",
          message: "❌ Failed to load QR display settings.",
        });
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [eventId]);

  const handleSave = async () => {
    if (!eventId) return;

    setSaving(true);

    try {
      const update = {
        useImageOnQr,
        lastSaved: Timestamp.now(),
      };

      await setDoc(
        doc(db, "events", eventId, "settings", "voting"),
        update,
        { merge: true }
      );

      setSettings((prev) => ({
        ...prev,
        ...update,
      }));

      showStatus(setStatus, {
        type: "success",
        message: "QR settings saved successfully.",
      });
    } catch (err) {
      console.error("❌ Failed to save QR settings:", err);

      showStatus(setStatus, {
        type: "error",
        message: "Failed to save QR settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  const lastSavedText = settings?.lastSaved?.toDate?.()
  ? settings.lastSaved.toDate().toLocaleString()
  : "Never";

  if (!ready || eventLoading || settingsLoading || isManager === null) {
    return <FullPageSpinner message="Loading QR settings..." />;
  }

  if (!canAccess) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1100px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">📷 QR Settings</h1>

            <p className="lead text-muted mb-0">
              Configure how QR codes are displayed and printed throughout your event.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save QR Settings"}
            </button>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert text-start ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>QR Display Options</strong>
          </div>

          <div className="card-body">
            <p className="text-muted mb-4">
              These settings affect printed QR cards used throughout the event.
            </p>

            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                id="useImageOnQr"
                checked={useImageOnQr}
                onChange={(e) => setUseImageOnQr(e.target.checked)}
              />

              <label
                className="form-check-label fw-bold"
                htmlFor="useImageOnQr"
              >
                Show Event Image Background
              </label>

              <div className="text-muted small mt-1">
                When enabled, QR print cards use the event image as the background.
                When disabled, QR cards use the standard clean layout.
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Preview Information</strong>
          </div>

          <div className="card-body">
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="fw-bold mb-2">
                    🖼 Background Enabled
                  </div>

                  <div className="small text-muted">
                    Event branding is incorporated into printed QR cards to create
                    a more visually appealing presentation.
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="fw-bold mb-2">
                    ⚪ Background Disabled
                  </div>

                  <div className="small text-muted">
                    Produces a clean and printer-friendly QR card with maximum QR
                    code contrast.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <strong>Save QR Settings</strong>

              <div className="small text-muted">
                Last saved: {lastSavedText}
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save QR Settings"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );

};

export default SettingsQr;